export const DEFAULT_DATE_FORMAT = "yyyy-MM-dd HH:mm:ss";

export type DateSeg =
  | { type: "token"; token: string; len: number }
  | { type: "lit"; text: string };

const TOKEN_LENS: Record<string, number> = {
  yyyy: 4,
  yy: 2,
  MM: 2,
  dd: 2,
  HH: 2,
  mm: 2,
  ss: 2,
};

const TOKENS = ["yyyy", "yy", "MM", "dd", "HH", "mm", "ss"];

export function tokenizeDateFormat(format: string): DateSeg[] {
  const segs: DateSeg[] = [];
  let i = 0;
  while (i < format.length) {
    const matched = TOKENS.find((t) => format.startsWith(t, i));
    if (matched) {
      segs.push({ type: "token", token: matched, len: TOKEN_LENS[matched] });
      i += matched.length;
    } else {
      const last = segs[segs.length - 1];
      if (last && last.type === "lit") {
        last.text += format[i];
      } else {
        segs.push({ type: "lit", text: format[i] });
      }
      i += 1;
    }
  }
  return segs;
}

export function isDateFormat(format: string): boolean {
  return tokenizeDateFormat(format).some((seg) => seg.type === "token");
}

export type DateFields = {
  year: boolean;
  month: boolean;
  day: boolean;
  hour: boolean;
  minute: boolean;
  second: boolean;
};

export function dateFieldsOf(segs: DateSeg[]): DateFields {
  const set = new Set(
    segs
      .filter((s): s is Extract<DateSeg, { type: "token" }> => s.type === "token")
      .map((s) => s.token),
  );
  return {
    year: set.has("yyyy") || set.has("yy"),
    month: set.has("MM"),
    day: set.has("dd"),
    hour: set.has("HH"),
    minute: set.has("mm"),
    second: set.has("ss"),
  };
}

export function hasTimeFields(fields: DateFields): boolean {
  return fields.hour || fields.minute || fields.second;
}

const pad = (value: number, len = 2) => String(value).padStart(len, "0");

export type DateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

export function formatPartsByPattern(parts: DateParts, segs: DateSeg[]): string {
  return segs
    .map((seg) => {
      if (seg.type === "lit") return seg.text;
      switch (seg.token) {
        case "yyyy":
          return pad(parts.year, 4);
        case "yy":
          return pad(parts.year % 100);
        case "MM":
          return pad(parts.month);
        case "dd":
          return pad(parts.day);
        case "HH":
          return pad(parts.hour);
        case "mm":
          return pad(parts.minute);
        case "ss":
          return pad(parts.second);
        default:
          return "";
      }
    })
    .join("");
}

export function formatByPattern(date: Date, segs: DateSeg[]): string {
  return formatPartsByPattern(
    {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds(),
    },
    segs,
  );
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function parsePartsByPattern(
  value: string,
  segs: DateSeg[],
  fallbackYear: number,
): DateParts | null {
  if (!value.trim()) return null;
  const order: string[] = [];
  let pattern = "^";
  for (const seg of segs) {
    if (seg.type === "lit") {
      pattern += escapeRegExp(seg.text);
    } else {
      pattern += `(\\d{1,${seg.len}})`;
      order.push(seg.token);
    }
  }
  pattern += "$";
  const match = value.match(new RegExp(pattern));
  if (!match) return null;

  const parts: DateParts = {
    year: fallbackYear,
    month: 1,
    day: 1,
    hour: 0,
    minute: 0,
    second: 0,
  };
  order.forEach((token, index) => {
    const num = Number(match[index + 1]);
    if (Number.isNaN(num)) return;
    switch (token) {
      case "yyyy":
        parts.year = num;
        break;
      case "yy":
        parts.year = 2000 + num;
        break;
      case "MM":
        parts.month = num;
        break;
      case "dd":
        parts.day = num;
        break;
      case "HH":
        parts.hour = num;
        break;
      case "mm":
        parts.minute = num;
        break;
      case "ss":
        parts.second = num;
        break;
    }
  });
  return parts;
}

export function parseByPattern(value: string, segs: DateSeg[]): Date | null {
  const parts = parsePartsByPattern(value, segs, new Date().getFullYear());
  if (!parts) return null;

  const date = new Date(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
  return Number.isNaN(date.getTime()) ? null : date;
}

export function applyDateMask(raw: string, segs: DateSeg[]): string {
  const digits = raw.replace(/\D/g, "");
  let out = "";
  let di = 0;
  for (const seg of segs) {
    if (seg.type === "lit") {
      if (di < digits.length) out += seg.text;
      else break;
    } else {
      if (di >= digits.length) break;
      const chunk = digits.slice(di, di + seg.len);
      out += chunk;
      di += chunk.length;
      if (chunk.length < seg.len) break;
    }
  }
  return out;
}

export type DateFieldConfig = {
  mode: "single" | "range";
  staticMode: boolean;
  shortcuts: boolean;
  min?: string;
  max?: string;
};

export function readDateConfig(
  options: { label: string; value: string }[],
): DateFieldConfig {
  const get = (key: string) => options.find((o) => o.label === key)?.value;
  return {
    mode: get("mode") === "range" ? "range" : "single",
    staticMode: get("static") === "1",
    shortcuts: get("shortcuts") === "1",
    min: get("min") || undefined,
    max: get("max") || undefined,
  };
}

export function buildDateOptions(
  config: DateFieldConfig,
): { label: string; value: string }[] {
  const out: { label: string; value: string }[] = [
    { label: "mode", value: config.mode },
  ];
  if (config.staticMode) out.push({ label: "static", value: "1" });
  if (config.shortcuts) out.push({ label: "shortcuts", value: "1" });
  if (config.min?.trim()) out.push({ label: "min", value: config.min.trim() });
  if (config.max?.trim()) out.push({ label: "max", value: config.max.trim() });
  return out;
}

export function mergeDateTime(
  base: Date,
  parts: Partial<{ hour: number; minute: number; second: number }>,
): Date {
  const next = new Date(base);
  if (parts.hour != null) next.setHours(parts.hour);
  if (parts.minute != null) next.setMinutes(parts.minute);
  if (parts.second != null) next.setSeconds(parts.second);
  return next;
}
